import React, { useMemo, useState, useEffect } from 'react';
import momentTimezone from 'moment-timezone';
import { chain, groupBy } from 'lodash';
import {
  array, bool, func, oneOfType, shape, string,
} from 'prop-types';
import { withTranslation } from '@playbookux/playbook-web-core';
import CannotTakeTestModal from '../can-not-take-test-modal';

import {
  Container,
  DateContainer,
  ContainerNoSlots,
  DayBox,
  Loader,
} from './styles';

const FORMAT_DATE = 'YYYY-MM-DD';
const FORMAT_DAY_NAME = 'dddd';
const FORMAT_MONTH_DAY = 'MMM DD';

const BodyTimeShow = (props) => {
  const {
    timeSlots,
    daysOfWeek,
    selectedTime,
    onTimeChange,
    timeZone,
    loading,
    history,
    timeFormat,
    dataAudience,
    phrases,
  } = props;

  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (!loading && dataAudience?.audienceCapacity?.isCalendarFull) {
      setOpenModal(true);
    }
  }, [loading, dataAudience]);

  const groupByDate = (timeArray, tz) => groupBy(timeArray, (date) => (
    momentTimezone(date.startAt).tz(tz).format(FORMAT_DATE)
  ));

  const groupedByDate = useMemo(() => {
    if (timeZone) {
      return groupByDate(timeSlots, timeZone);
    }
    return [];
  }, [timeZone, timeSlots]);

  const dateColumns = useMemo(() => daysOfWeek.map((element) => {
    const dateOfWeek = element.format(FORMAT_DATE);
    const dayName = element.format(FORMAT_DAY_NAME);
    const monthDay = element.format(FORMAT_MONTH_DAY);
    const timeSlotsValue = chain(groupedByDate[dateOfWeek])
      .map('startAt')
      .sortBy()
      .value();

    return {
      date: element,
      timeSlots: timeSlotsValue,
      dayName,
      monthDay,
    };
  }), [daysOfWeek, groupedByDate]);

  const timeSlotsInWeek = useMemo(() => chain(dateColumns)
    .map('timeSlots')
    .flatten()
    .value(),
  [dateColumns]);

  if (loading) {
    return (<ContainerNoSlots><Loader size="small" data-loader="time-loader" /></ContainerNoSlots>);
  }

  if (openModal) {
    return (
      <CannotTakeTestModal
        open={openModal}
        history={history}
      />
    );
  }

  if (timeSlotsInWeek.length === 0) {
    return (<ContainerNoSlots>{phrases.no_times_available}</ContainerNoSlots>);
  }

  return (
    <Container aria-label="Time slots">
      {dateColumns.map((week, index) => (
        <DateContainer key={`${index * 1}-line`}>
          {week.timeSlots.map((timeSlot) => (
            <DayBox
              key={`${timeSlot}-cell`}
              tabIndex="0"
              role="option"
              selectedDay={selectedTime === timeSlot}
              onClick={() => onTimeChange(timeSlot)}
              aria-label={`${momentTimezone(timeSlot).tz(timeZone).format(timeFormat)} ${week.dayName} ${week.monthDay}`}
              aria-selected={selectedTime === timeSlot}
            >
              {momentTimezone(timeSlot).tz(timeZone).format(timeFormat)}
            </DayBox>
          ))}
        </DateContainer>
      ))}
    </Container>
  );
};

BodyTimeShow.propTypes = {
  timeSlots: oneOfType([array]).isRequired,
  daysOfWeek: oneOfType([array]).isRequired,
  selectedTime: string,
  onTimeChange: func.isRequired,
  timeZone: string,
  loading: bool.isRequired,
  dataAudience: shape(),
  timeFormat: string.isRequired,
  history: shape({
    push: func,
  }).isRequired,
  phrases: shape({
    no_times_available: string,
  }).isRequired,
};

BodyTimeShow.defaultProps = {
  timeZone: null,
  selectedTime: null,
  dataAudience: {},
};

export default withTranslation(BodyTimeShow);
